/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Kirill Vizniuk - kirill@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {Component, OnDestroy, OnInit} from '@angular/core';
import {ReportService} from '../../../../services/report.service';
import {ReportProvider, ReportLinksProvider } from '../../../../providers';
import {Report, Link, Helpers} from '../../../../model';
import {StageService} from '../../../../services';
import {BsModalService, BsModalRef} from 'ngx-bootstrap/modal';
import {Router, ActivatedRoute} from '@angular/router';
import { Subject } from 'rxjs';
import { EditLinkModal, ConfirmDeleteLinkModal } from '../../report/modals';
import {LinkType} from '../../../../model/report';
import { ClipboardService } from 'ngx-clipboard';
import { ModuleType } from 'src/app/model/enums/module.type';
import {LogService } from 'src/app/services';


@Component({
  selector: 'page-report-ea-links',
  templateUrl: 'report-ea.links.page.html',
  styleUrls: ['report-ea.links.page.scss']
})
export class ReportEaLinksPage implements OnInit, OnDestroy {
  currentReport: Report = new Report();
  private reportSub: any;
  totalItems = 0;
  currentPage = 1;
  links: Link[] = [];
  dateCreated: Date = new Date();

  selectAll = false;
  isLoading = false;

  editModal: BsModalRef;
  deleteModal: BsModalRef;

  createDeleteFlag = false;

  LinkType = LinkType;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private reportProvider: ReportProvider,
    private reportService: ReportService,
    private modalService: BsModalService,
    private stageService: StageService,
    private reportLinkProvider: ReportLinksProvider,
    private clipboardService: ClipboardService,
    private logService : LogService
  ) {}

  ngOnInit(): void {
    this.reportSub = this.reportService.report$.subscribe(currentReport => {
      this.currentReport = currentReport;

      if (this.currentReport) {
        this.createDeleteFlag  = this.currentReport.STAGE.NAME === 'Report Submitted';
      }

      this.loadLinks();
    });

  }

  ngOnDestroy(): void {
    if (this.reportSub) this.reportSub.unsubscribe();
  }

  private loadLinks(): void {
    if (this.currentReport) {
      this.isLoading = true;
      this.reportLinkProvider.getLinks(this.currentReport)
        .subscribe(
          data => {
            if (data.success) {
              this.links = Link.parseArray(data.result);
            }
            this.isLoading = false;
          }, () => {
            this.isLoading = false;
          });
    }
  }

  onCreateLink(): void {
    this.reportLinkProvider.createLink(this.currentReport, ModuleType.PMA).subscribe(data => {
      if (data.success) {
        this.links.push(Link.parse(data.result));
        this.totalItems = this.links.length;
      }
    });
  }

  onDeleteLink(link: Link, index: number): void {
    this.deleteModal = this.modalService.show(ConfirmDeleteLinkModal, Helpers.getModalConfig());
    const ackSubject = new Subject<boolean>();
    this.deleteModal.content.subject = ackSubject;

    ackSubject.subscribe(value => {
      if (value) {
        this.reportLinkProvider.deleteLink(this.currentReport, link.ID)
          .subscribe(data => {
            if (data.success) {
              this.links.splice(index, 1);
            }
          });
      }
    });
  }

  onEditLink(link: Link): void {
    this.editModal = this.modalService.show(EditLinkModal, Helpers.getModalConfig());
    const ackSubject = new Subject<boolean>();
    this.editModal.content.subject = ackSubject;
    this.editModal.content.link = link;
    this.editModal.content.currentReport = this.currentReport;

    ackSubject.subscribe(value => {
      if (value) {
        this.logService.log(value);
      }
    });
  }

  pageChanged(event: any): void {
    if (event.page !== this.currentPage)
      this.loadLinks();
  }

  get hostName(): string {
    return window.location.protocol + '//' + window.location.host.replace('4200', '5000');
  }

  get hostPath(): string {
    return '/link/';
  }

  copy(link: Link): void{
    this.clipboardService.copyFromContent(this.hostName + this.hostPath + link.HASH);

    link.copied = true;
    setTimeout(() => {
      link.copied = false;
    }, 1500);
  }

  getTrackerLinkURL(link: Link): string {
    return `${this.hostName}/tracker/${link.ID}`;
  }
}
